import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import { usePostHog } from '@posthog/react';
import { ANALYTICS_EVENTS } from './events.js';
import { captureGated } from './hooks.js';
import { useConsentMode } from './provider.js';
/**
 * Class component that does the actual catching (React error boundaries must
 * be classes). Takes the PostHog client as a prop rather than reading it
 * itself so it never needs its own context read — see AnalyticsErrorBoundary
 * below, which supplies it via usePostHog().
 */
export class AnalyticsErrorBoundaryInner extends Component {
    constructor() {
        super(...arguments);
        this.state = { hasError: false, error: null };
        this.reset = () => {
            this.setState({ hasError: false, error: null });
        };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        console.error('AnalyticsErrorBoundary caught error:', error, errorInfo);
        // Consent-gated, never-throwing capture (captureGated in hooks.ts, BR-1).
        // Always report error_type 'unknown' and the configured surface/app — NEVER error.message
        // or error.stack — this boundary only catches uncaught render errors, not explicit
        // try/catch call sites that already fire a specific event.
        const payload = {
            error_type: 'unknown',
            surface: this.props.surface,
            app: this.props.app,
        };
        captureGated(this.props.posthog, this.props.consentMode, ANALYTICS_EVENTS.ERROR_SHOWN, payload);
        // Test-only observability hook (ERR-1/ERR-2): records the exact event name + payload
        // this boundary constructs, independent of whether a real PostHog client is mounted.
        // Every web app disables AnalyticsProvider outside production (`enabled={import.meta.env.PROD}`)
        // and ships no VITE_POSTHOG_KEY in dev/test builds (Business Rule 5), so `posthog` above is
        // always `undefined` there and the real capture call above is structurally a no-op — this is
        // the only way an e2e test can observe what error_shown would have carried. A complete no-op
        // (single property check) unless a test has explicitly installed the hook via
        // `page.addInitScript` before navigation; never present in production.
        if (typeof window !== 'undefined') {
            window.__RESIDA_E2E_ANALYTICS__?.record(ANALYTICS_EVENTS.ERROR_SHOWN, payload);
        }
    }
    render() {
        return this.state.hasError
            ? this.props.fallback(this.state.error, this.reset)
            : this.props.children;
    }
}
export function AnalyticsErrorBoundary(props) {
    const posthog = usePostHog();
    const consentMode = useConsentMode();
    return _jsx(AnalyticsErrorBoundaryInner, { ...props, posthog: posthog, consentMode: consentMode });
}
//# sourceMappingURL=error-boundary.js.map